/**
 * Collection popup.
 * Opens its dialog after `data-delay` seconds, once per visit (browser session). Waits while another popup
 * or drawer is open so it never covers the cart or the login window. `data-preview` opens it straight away
 * (used in the theme editor) and does not remember it as seen.
 */
class CollectionPopup extends HTMLElement {
  /** @type {number | undefined} */
  #timer;

  #abortController = new AbortController();

  connectedCallback() {
    this.dialog = /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-dialog]'));
    if (!this.dialog || typeof this.dialog.showModal !== 'function') return;

    const { signal } = this.#abortController;
    this.storageKey = this.dataset.storageKey || 'collection-popup';
    this.preview = this.hasAttribute('data-preview');

    this.querySelectorAll('[data-close]').forEach((button) =>
      button.addEventListener('click', () => this.close(), { signal })
    );

    // A click on the dimmed area around the popup closes it
    this.dialog.addEventListener(
      'click',
      (event) => {
        if (event.target === this.dialog) this.close();
      },
      { signal }
    );

    if (this.preview) {
      this.open();
      return;
    }

    if (this.#hasBeenSeen()) return;

    const delay = Math.max(0, Number(this.dataset.delay) || 15) * 1000;
    this.#timer = window.setTimeout(() => this.#openWhenFree(), delay);
  }

  disconnectedCallback() {
    window.clearTimeout(this.#timer);
    this.#abortController.abort();
    if (this.dialog?.open) this.dialog.close();
  }

  open() {
    if (!this.dialog || this.dialog.open) return;
    this.dialog.showModal();
    if (!this.preview) this.#markSeen();
  }

  close() {
    this.dialog?.close();
  }

  #openWhenFree() {
    // Another dialog (login, cart drawer, search) is open: try again a little later
    const otherDialogOpen = Array.from(document.querySelectorAll('dialog[open]')).some(
      (dialog) => dialog !== this.dialog
    );

    if (otherDialogOpen) {
      this.#timer = window.setTimeout(() => this.#openWhenFree(), 5000);
      return;
    }

    this.open();
  }

  #hasBeenSeen() {
    try {
      return window.sessionStorage.getItem(this.storageKey) === 'seen';
    } catch {
      return false;
    }
  }

  #markSeen() {
    try {
      window.sessionStorage.setItem(this.storageKey, 'seen');
    } catch {
      // Storage unavailable (private mode): the popup may show again on the next page of the visit
    }
  }
}

if (!customElements.get('collection-popup')) {
  customElements.define('collection-popup', CollectionPopup);
}
